import { lazy, Suspense } from "react";
import { Outlet, type RouteObject } from "react-router-dom";
import { AuthProvider } from "@/pages/admin/context/AuthContext";
import AuthGuard from "@/pages/admin/components/AuthGuard";

// Carrega as páginas sob demanda (code splitting): o navegador baixa só o
// código da página que a pessoa está abrindo, deixando o site bem mais leve.
const DynamicPage = lazy(() => import("@/pages/DynamicPage"));
const Home = lazy(() => import("@/pages/home/page"));
const Empresa = lazy(() => import("@/pages/empresa/page"));
const Produtos = lazy(() => import("@/pages/produtos/page"));
const EmbalagensDePapelao = lazy(() => import("@/pages/embalagens-de-papelao/page"));
const EmbalagensPersonalizadas = lazy(() => import("@/pages/embalagens-personalizadas/page"));
const EmbalagensParaDoce = lazy(() => import("@/pages/embalagens-para-doce/page"));
const EmbalagensParaDocesESalgadosComEngates = lazy(() => import("@/pages/embalagens-para-doces-e-salgados-com-engates/page"));
const EmbalagensDeEmpilhamento = lazy(() => import("@/pages/embalagens-de-empilhamento/page"));
const EmbalagensDePizza = lazy(() => import("@/pages/embalagens-de-pizza/page"));
const EmbalagensParaTorta = lazy(() => import("@/pages/embalagens-para-torta/page"));
const EmbalagensParaCupCake = lazy(() => import("@/pages/embalagens-para-cup-cake/page"));
const EmbalagensParaArquivoMorto = lazy(() => import("@/pages/embalagens-para-arquivo-morto/page"));
const EmbalagensParaSorvete = lazy(() => import("@/pages/embalagens-para-sorvete/page"));
const CaixasParaPrateleira = lazy(() => import("@/pages/caixas-para-prateleira/page"));
const EmbalagensParaParafuso = lazy(() => import("@/pages/embalagens-para-parafuso/page"));
const Fotos = lazy(() => import("@/pages/fotos/page"));
const Videos = lazy(() => import("@/pages/videos/page"));
const NovasMaquinas = lazy(() => import("@/pages/novas-maquinas/page"));
const GaleriaDeVideos = lazy(() => import("@/pages/galeria-de-videos/page"));
const Contato = lazy(() => import("@/pages/contato/page"));
const SejaRepresentante = lazy(() => import("@/pages/seja-representante/page"));
const AdminLogin = lazy(() => import("@/pages/admin/login/page"));
const AdminDashboard = lazy(() => import("@/pages/admin/page"));
const AdminProducts = lazy(() => import("@/pages/admin/produtos/page"));
const AdminProductEdit = lazy(() => import("@/pages/admin/produtos/editar/page"));
const AdminCompany = lazy(() => import("@/pages/admin/empresa/page"));
const AdminGallery = lazy(() => import("@/pages/admin/galeria/page"));
const AdminVideos = lazy(() => import("@/pages/admin/videos/page"));
const AdminHero = lazy(() => import("@/pages/admin/hero/page"));
const AdminFaq = lazy(() => import("@/pages/admin/faq/page"));
const AdminUsers = lazy(() => import("@/pages/admin/usuarios/page"));
const AdminMenus = lazy(() => import("@/pages/admin/menus/page"));
const AdminPaginas = lazy(() => import("@/pages/admin/paginas/page"));
const AdminGlobalSeo = lazy(() => import("@/pages/admin/seo-global/page"));
const AdminRedirects = lazy(() => import("@/pages/admin/redirecionamentos/page"));
const AdminHome = lazy(() => import("@/pages/admin/home/page"));
const AdminAparencia = lazy(() => import("@/pages/admin/aparencia/page"));
const AdminContacts = lazy(() => import("@/pages/admin/contatos/page"));
const AdminEmail = lazy(() => import("@/pages/admin/email/page"));
const AdminBackup = lazy(() => import("@/pages/admin/backup/page"));
const AdminLogs = lazy(() => import("@/pages/admin/logs/page"));
const AdminAnalytics = lazy(() => import("@/pages/admin/analytics/page"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50">
      <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
    </div>
  );
}

const routes: RouteObject[] = [
  { path: "/", element: <Suspense fallback={<RouteFallback />}><Home /></Suspense> },
  { path: "/empresa", element: <Suspense fallback={<RouteFallback />}><Empresa /></Suspense> },
  { path: "/produtos", element: <Suspense fallback={<RouteFallback />}><Produtos /></Suspense> },
  { path: "/embalagens-de-papelao", element: <Suspense fallback={<RouteFallback />}><EmbalagensDePapelao /></Suspense> },
  { path: "/embalagens-personalizadas", element: <Suspense fallback={<RouteFallback />}><EmbalagensPersonalizadas /></Suspense> },
  { path: "/embalagens-para-doce", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaDoce /></Suspense> },
  { path: "/embalagens-para-doces-e-salgados-com-engates", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaDocesESalgadosComEngates /></Suspense> },
  { path: "/embalagens-de-empilhamento", element: <Suspense fallback={<RouteFallback />}><EmbalagensDeEmpilhamento /></Suspense> },
  { path: "/embalagens-de-pizza", element: <Suspense fallback={<RouteFallback />}><EmbalagensDePizza /></Suspense> },
  { path: "/embalagens-para-torta", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaTorta /></Suspense> },
  { path: "/embalagens-para-cup-cake", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaCupCake /></Suspense> },
  { path: "/embalagens-para-arquivo-morto", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaArquivoMorto /></Suspense> },
  { path: "/embalagens-para-sorvete", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaSorvete /></Suspense> },
  { path: "/caixas-para-prateleira", element: <Suspense fallback={<RouteFallback />}><CaixasParaPrateleira /></Suspense> },
  { path: "/embalagens-para-parafuso", element: <Suspense fallback={<RouteFallback />}><EmbalagensParaParafuso /></Suspense> },
  { path: "/fotos", element: <Suspense fallback={<RouteFallback />}><Fotos /></Suspense> },
  { path: "/videos", element: <Suspense fallback={<RouteFallback />}><Videos /></Suspense> },
  { path: "/novas-maquinas", element: <Suspense fallback={<RouteFallback />}><NovasMaquinas /></Suspense> },
  { path: "/galeria-de-videos", element: <Suspense fallback={<RouteFallback />}><GaleriaDeVideos /></Suspense> },
  { path: "/contato", element: <Suspense fallback={<RouteFallback />}><Contato /></Suspense> },
  { path: "/seja-representante", element: <Suspense fallback={<RouteFallback />}><SejaRepresentante /></Suspense> },
  {
    path: "/admin/login",
    element: (
      <AuthProvider>
        <Suspense fallback={<RouteFallback />}><AdminLogin /></Suspense>
      </AuthProvider>
    ),
  },
  {
    path: "/admin",
    element: (
      <AuthProvider>
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      </AuthProvider>
    ),
    children: [
      { index: true, element: <Suspense fallback={<RouteFallback />}><AdminDashboard /></Suspense> },
      { path: "produtos", element: <Suspense fallback={<RouteFallback />}><AdminProducts /></Suspense> },
      { path: "produtos/novo", element: <Suspense fallback={<RouteFallback />}><AdminProductEdit /></Suspense> },
      { path: "produtos/editar/:id", element: <Suspense fallback={<RouteFallback />}><AdminProductEdit /></Suspense> },
      { path: "empresa", element: <Suspense fallback={<RouteFallback />}><AdminCompany /></Suspense> },
      { path: "galeria", element: <Suspense fallback={<RouteFallback />}><AdminGallery /></Suspense> },
      { path: "videos", element: <Suspense fallback={<RouteFallback />}><AdminVideos /></Suspense> },
      { path: "hero", element: <Suspense fallback={<RouteFallback />}><AdminHero /></Suspense> },
      { path: "faq", element: <Suspense fallback={<RouteFallback />}><AdminFaq /></Suspense> },
      { path: "usuarios", element: <Suspense fallback={<RouteFallback />}><AdminUsers /></Suspense> },
      { path: "menus", element: <Suspense fallback={<RouteFallback />}><AdminMenus /></Suspense> },
      { path: "paginas", element: <Suspense fallback={<RouteFallback />}><AdminPaginas /></Suspense> },
      { path: "seo-global", element: <Suspense fallback={<RouteFallback />}><AdminGlobalSeo /></Suspense> },
      { path: "redirecionamentos", element: <Suspense fallback={<RouteFallback />}><AdminRedirects /></Suspense> },
      { path: "home", element: <Suspense fallback={<RouteFallback />}><AdminHome /></Suspense> },
      { path: "aparencia", element: <Suspense fallback={<RouteFallback />}><AdminAparencia /></Suspense> },
      { path: "contatos", element: <Suspense fallback={<RouteFallback />}><AdminContacts /></Suspense> },
      { path: "email", element: <Suspense fallback={<RouteFallback />}><AdminEmail /></Suspense> },
      { path: "backup", element: <Suspense fallback={<RouteFallback />}><AdminBackup /></Suspense> },
      { path: "analytics", element: <Suspense fallback={<RouteFallback />}><AdminAnalytics /></Suspense> },
      { path: "logs", element: <Suspense fallback={<RouteFallback />}><AdminLogs /></Suspense> },
    ],
  },
  { path: "*", element: <Suspense fallback={<RouteFallback />}><DynamicPage /></Suspense> },
];

export default routes;